// MIT License - Copyright (c) 2026 Doug McNall - See .kiro/steering/general/development-standards.md for full license

/**
 * Story Arc Manager
 * 
 * Multi-step quest chains spanning multiple star systems.
 * Each arc has 3-5 steps with narrative, objectives, and escalating rewards.
 */

const STORAGE_KEY = 'storyArcs';

const ARC_STATUS = { AVAILABLE: 'available', ACTIVE: 'active', COMPLETED: 'completed' };
const STEP_STATUS = { LOCKED: 'locked', ACTIVE: 'active', COMPLETED: 'completed' };

// ============================================================================
// ARC TEMPLATES
// ============================================================================

const ARC_TEMPLATES = [
  {
    id: 'distress_signal',
    title: 'The Distress Signal',
    description: 'A mysterious distress signal leads you across the sector on a rescue mission that becomes something far more dangerous.',
    icon: '📡',
    steps: [
      { objective: 'scan', title: 'Trace the Signal', narrative: 'You detect a faint distress signal from deep space. Scan the source system to triangulate its origin.', reward: 300 },
      { objective: 'travel', title: 'Investigate the Source', narrative: 'The signal originates from a derelict station. Travel there to investigate.', reward: 500 },
      { objective: 'combat', title: 'Ambush!', narrative: 'It was a trap! Pirates are using the signal as bait. Fight your way out.', kills: 3, reward: 800 },
      { objective: 'travel', title: 'Return the Evidence', narrative: 'You recovered pirate intel from the ambush. Return to a station to report your findings.', reward: 1200 },
    ],
    totalReputation: 25,
  },
  {
    id: 'lost_expedition',
    title: 'The Lost Expedition',
    description: 'A scientific expedition went silent three systems away. Retrace their steps and uncover what happened.',
    icon: '🔬',
    steps: [
      { objective: 'travel', title: 'Last Known Position', narrative: 'Travel to the expedition\'s last known coordinates.', reward: 400 },
      { objective: 'scan', title: 'Search for Clues', narrative: 'Scan the system for traces of the expedition\'s ship.', reward: 400 },
      { objective: 'travel', title: 'Follow the Trail', narrative: 'Sensor data shows they jumped to a nearby system. Follow their trail.', reward: 500 },
      { objective: 'scan', title: 'The Discovery', narrative: 'You find the expedition ship orbiting an uncharted planet. Scan to assess the situation.', reward: 600 },
      { objective: 'travel', title: 'Bring Them Home', narrative: 'The crew is alive but stranded. Escort them back to the nearest station.', reward: 1500 },
    ],
    totalReputation: 35,
  },
  {
    id: 'trade_war',
    title: 'The Trade War',
    description: 'Two rival factions are manipulating trade routes. Play both sides or pick one — your choice shapes the outcome.',
    icon: '💰',
    steps: [
      { objective: 'trade', title: 'The Offer', narrative: 'A merchant offers you a lucrative deal: deliver rare goods to a contested system.', good: 'rareMinerals', qty: 5, reward: 600 },
      { objective: 'travel', title: 'The Handoff', narrative: 'Deliver the goods to the buyer at the destination system.', reward: 800 },
      { objective: 'combat', title: 'Intercepted', narrative: 'The rival faction sends ships to stop you. Defend yourself!', kills: 2, reward: 1000 },
      { objective: 'travel', title: 'Collect Your Pay', narrative: 'Return to a station to collect your payment and choose your allegiance.', reward: 2000 },
    ],
    totalReputation: 30,
  },
  {
    id: 'anomaly_chain',
    title: 'The Anomaly Chain',
    description: 'Strange energy readings are appearing across multiple systems. Each one leads to the next, forming a pattern that could change everything.',
    icon: '🌀',
    steps: [
      { objective: 'scan', title: 'First Contact', narrative: 'An unusual energy signature appears on your sensors. Scan it.', reward: 500 },
      { objective: 'travel', title: 'Second Signal', narrative: 'The scan reveals coordinates to another anomaly. Travel there.', reward: 500 },
      { objective: 'scan', title: 'The Pattern', narrative: 'A third anomaly. They\'re forming a geometric pattern across the sector.', reward: 700 },
      { objective: 'travel', title: 'The Convergence', narrative: 'All signals point to a single location. Travel to the convergence point.', reward: 1000 },
      { objective: 'scan', title: 'The Revelation', narrative: 'At the convergence, you discover an ancient alien beacon. Scan it to decode its message.', reward: 2000 },
    ],
    totalReputation: 40,
  },
  {
    id: 'bounty_hunter',
    title: 'Bounty Hunter',
    description: 'A high-value target is fleeing across the sector. Track them down system by system.',
    icon: '🎯',
    steps: [
      { objective: 'travel', title: 'Pick Up the Trail', narrative: 'Intelligence reports place the target in a nearby system. Head there.', reward: 400 },
      { objective: 'scan', title: 'Narrow the Search', narrative: 'Scan the system to locate the target\'s ship signature.', reward: 400 },
      { objective: 'travel', title: 'The Chase', narrative: 'They\'ve jumped! Follow them to the next system before the trail goes cold.', reward: 600 },
      { objective: 'combat', title: 'Cornered', narrative: 'You\'ve caught up. The target fights back with hired mercenaries.', kills: 4, reward: 1500 },
    ],
    totalReputation: 30,
  },
];

// ============================================================================
// PERSISTENCE
// ============================================================================

const load = () => {
  try { return JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}'); }
  catch { return {}; }
};

const save = (data) => {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
};

// ============================================================================
// ARC GENERATION
// ============================================================================

/**
 * Generate a story arc from a template, assigning real systems to each step.
 * @param {string} templateId
 * @param {Array} starSystems - Available star systems
 * @param {string} currentSystemName - Player's current system name
 * @returns {Object} Generated arc
 */
export const generateArc = (templateId, starSystems, currentSystemName) => {
  const template = ARC_TEMPLATES.find(t => t.id === templateId);
  if (!template) return null;

  const systemNames = starSystems
    .map(s => s.name)
    .filter(n => n !== currentSystemName);

  // Assign a different system to each step
  const shuffled = [...systemNames].sort(() => Math.random() - 0.5);
  const steps = template.steps.map((step, i) => ({
    ...step,
    id: `${template.id}_step_${i}`,
    targetSystem: shuffled[i % shuffled.length],
    status: i === 0 ? STEP_STATUS.ACTIVE : STEP_STATUS.LOCKED,
    killCount: 0,
  }));

  return {
    id: `arc_${template.id}_${Date.now()}`,
    templateId: template.id,
    title: template.title,
    description: template.description,
    icon: template.icon,
    steps,
    currentStep: 0,
    status: ARC_STATUS.ACTIVE,
    totalReputation: template.totalReputation,
    startedAt: Date.now(),
  };
};

// ============================================================================
// ARC MANAGEMENT
// ============================================================================

/** Get all arcs (active + completed) */
export const getArcs = () => {
  const data = load();
  return data.arcs || [];
};

/** Get active arcs only */
export const getActiveArcs = () => getArcs().filter(a => a.status === ARC_STATUS.ACTIVE);

/** Get completed arcs */
export const getCompletedArcs = () => getArcs().filter(a => a.status === ARC_STATUS.COMPLETED);

/** Start a new arc */
export const startArc = (templateId, starSystems, currentSystemName) => {
  const data = load();
  if (!data.arcs) data.arcs = [];

  // Don't allow duplicate active arcs of same template
  if (data.arcs.some(a => a.templateId === templateId && a.status === ARC_STATUS.ACTIVE)) return null;

  const arc = generateArc(templateId, starSystems, currentSystemName);
  if (!arc) return null;

  data.arcs.push(arc);
  save(data);
  return arc;
};

/** Get available arc templates (not currently active) */
export const getAvailableTemplates = () => {
  const activeIds = getActiveArcs().map(a => a.templateId);
  return ARC_TEMPLATES.filter(t => !activeIds.includes(t.id));
};

/**
 * Advance an arc's current step. Called when the player completes a step objective.
 * @param {string} arcId
 * @returns {Object|null} { arc, stepCompleted, arcCompleted, rewards }
 */
export const advanceStep = (arcId) => {
  const data = load();
  const arc = (data.arcs || []).find(a => a.id === arcId);
  if (!arc || arc.status !== ARC_STATUS.ACTIVE) return null;

  const step = arc.steps[arc.currentStep];
  if (!step || step.status !== STEP_STATUS.ACTIVE) return null;

  // Mark step completed
  step.status = STEP_STATUS.COMPLETED;
  const stepReward = step.reward || 0;

  // Check if arc is complete
  if (arc.currentStep >= arc.steps.length - 1) {
    arc.status = ARC_STATUS.COMPLETED;
    arc.completedAt = Date.now();
    save(data);
    return { arc, stepCompleted: step, arcCompleted: true, rewards: { credits: stepReward, reputation: arc.totalReputation } };
  }

  // Unlock next step
  arc.currentStep++;
  arc.steps[arc.currentStep].status = STEP_STATUS.ACTIVE;
  save(data);
  return { arc, stepCompleted: step, arcCompleted: false, rewards: { credits: stepReward, reputation: 0 } };
};

// ============================================================================
// OBJECTIVE CHECKING — called by game systems
// ============================================================================

/**
 * Check if arriving at a system completes any active arc steps.
 * @param {string} systemName
 * @returns {Array} Array of { arcId, step } for completed steps
 */
export const checkTravelObjective = (systemName) => {
  const results = [];
  getActiveArcs().forEach(arc => {
    const step = arc.steps[arc.currentStep];
    if (step?.status === STEP_STATUS.ACTIVE && step.objective === 'travel' && step.targetSystem === systemName) {
      results.push({ arcId: arc.id, step });
    }
  });
  return results;
};

/**
 * Check if scanning a system completes any active arc steps.
 * @param {string} systemName
 * @returns {Array}
 */
export const checkScanObjective = (systemName) => {
  const results = [];
  getActiveArcs().forEach(arc => {
    const step = arc.steps[arc.currentStep];
    if (step?.status === STEP_STATUS.ACTIVE && step.objective === 'scan' && step.targetSystem === systemName) {
      results.push({ arcId: arc.id, step });
    }
  });
  return results;
};

/**
 * Record a combat kill and check if it completes any arc steps.
 * @returns {Array}
 */
export const checkCombatObjective = () => {
  const data = load();
  const results = [];
  (data.arcs || []).forEach(arc => {
    if (arc.status !== ARC_STATUS.ACTIVE) return;
    const step = arc.steps[arc.currentStep];
    if (step?.status === STEP_STATUS.ACTIVE && step.objective === 'combat') {
      step.killCount = (step.killCount || 0) + 1;
      if (step.killCount >= (step.kills || 3)) {
        results.push({ arcId: arc.id, step });
      }
    }
  });
  save(data);
  return results;
};

/** Export templates for UI */
export const ARC_TEMPLATE_LIST = ARC_TEMPLATES;

export { ARC_STATUS, STEP_STATUS };
